import { useState, useRef, useEffect, useLayoutEffect, useId } from "react";
import { ChevronDown, Check, Repeat } from "lucide-react";
import { accountLabel, accountName, useAccounts } from "@/hooks/useAccounts";
import { useAuthStore } from "@/store/useAuthStore";

/**
 * Scope control to switch into a co-teach target (or, for org admins, any org
 * teacher). Hidden when the caller has only their own account. Selecting an
 * account persists the view-as target and reloads to re-scope all views.
 */
const AccountSwitcher = () => {
  const { accounts } = useAccounts();
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const setActingTeacher = useAuthStore((s) => s.setActingTeacher);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const [menuPosition, setMenuPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    const positionMenu = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const gutter = 16;
      const width = Math.min(320, window.innerWidth - gutter * 2);
      const spaceBelow = window.innerHeight - rect.top - gutter;
      const spaceAbove = rect.bottom - gutter;
      // The trigger sits low in the rail, above the profile entry: there the
      // menu grows upward from the trigger's bottom edge, so a short list
      // stays beside it instead of floating at a fixed height above it.
      const opensUpward = spaceAbove > spaceBelow;
      setMenuPosition({
        width,
        maxHeight: Math.min(480, opensUpward ? spaceAbove : spaceBelow),
        left: Math.max(
          gutter,
          Math.min(rect.right + 12, window.innerWidth - width - gutter),
        ),
        ...(opensUpward
          ? { bottom: window.innerHeight - rect.bottom }
          : { top: rect.top }),
      });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // No switcher unless there's somewhere to switch to.
  if (accounts.length <= 1) return null;

  const own = accounts.find((a) => a.is_you);
  // Your own account always renders first; the rest keep server order.
  const ordered = [...accounts]
    .filter((account) =>
      `${accountName(account)} ${account.username || ""}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
    )
    .sort((a, b) => (a.is_you === b.is_you ? 0 : a.is_you ? -1 : 1));
  const selected = actingTeacherId
    ? accounts.find((a) => a.teacher_id === actingTeacherId)
    : own;
  const label = !selected
    ? ""
    : selected.is_you
      ? `${accountName(selected)} (You)`
      : accountLabel(selected);

  const handleSelect = (teacherId: number, isYou: boolean) => {
    setIsOpen(false);
    const next = isYou ? null : teacherId;
    if (next === actingTeacherId) return;
    setActingTeacher(next);
  };

  return (
    <div
      className="relative et-sidebar-switcher"
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === "Escape" && isOpen) {
          e.stopPropagation();
          setIsOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        type="button"
        ref={trigger}
        aria-label={label || "Switch account"}
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        className="text-[#28543D] bg-white shadow-[0_0px_5px_rgba(40,84,61,0.20)] h-[50px] gap-[8px] rounded-[14px] py-[12px] px-[16px] flex flex-row items-center max-w-[260px]"
        onClick={() => {
          setQuery("");
          setIsOpen((v) => !v);
        }}
        title="Switch account"
      >
        <Repeat size={24} stroke="#28543D" strokeWidth={2.25} />
        <span className="font-nunito font-semibold text-[16px] leading-none truncate">
          {label}
        </span>
        <ChevronDown size={16} stroke="#28543D" strokeWidth={2} />
      </button>

      {isOpen && (
        <div
          id={menuId}
          style={menuPosition}
          className={`et-account-menu absolute right-0 mt-2 w-[300px] bg-[#FBF8F1] rounded-[8px] shadow-[0_8px_30px_rgba(16,24,40,0.12)] border border-[#DED8CC] p-[12px] flex flex-col transition-all duration-200 ease-out origin-top z-50 max-h-[400px] overflow-y-auto ${
            isOpen
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
          }`}
        >
          <div className="px-1 pt-1 pb-2 font-mulish font-bold text-[11px] tracking-[0.08em] text-[#687368] uppercase">
            Switch account
          </div>
          <input
            type="search"
            aria-label="Search accounts"
            placeholder="Search accounts…"
            className="et-account-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {ordered.map((account) => {
            const isSelected = account.is_you
              ? !actingTeacherId
              : account.teacher_id === actingTeacherId;
            const displayName = accountName(account);
            const username = account.username?.trim();
            const subtitle = [
              username === displayName ? null : username,
              account.is_you ? "You" : null,
            ]
              .filter(Boolean)
              .join(" · ");
            const actingAsOther = !!actingTeacherId;
            return (
              <button
                type="button"
                key={account.teacher_id}
                aria-pressed={isSelected}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[6px] transition-colors text-left ${
                  account.is_you && actingAsOther
                    ? "bg-[#EEF0E3] hover:bg-[#E2E8D8]"
                    : "hover:bg-[#F3EDE2]"
                }`}
                onClick={() => handleSelect(account.teacher_id, account.is_you)}
              >
                <span
                  className={`flex-shrink-0 flex items-center justify-center w-[44px] h-[44px] rounded-full font-mulish font-bold text-[16px] ${
                    account.is_you
                      ? "bg-[#EEF0E3] text-[#28543D]"
                      : "bg-[#F1E8D2] text-[#8A6A2F]"
                  }`}
                >
                  {initials(
                    account.name || account.username || account.email || "",
                  )}
                </span>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="font-mulish font-bold text-[16px] leading-tight text-[#294B39] truncate">
                    {displayName}
                  </span>
                  {subtitle && (
                    <span className="font-mulish text-[13px] leading-tight text-[#687368] truncate">
                      {subtitle}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <Check size={20} stroke="#28543D" strokeWidth={2.5} />
                )}
              </button>
            );
          })}
          {ordered.length === 0 && (
            <p role="status" className="et-account-empty">
              No accounts match your search.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/** Up to two uppercase initials from a name (falls back to first char). */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default AccountSwitcher;
